import React from "react";
import { useSelector } from "react-redux";

const TermsAndConditionsComponent = ({ fee, mentorName }) => {
  const { firstName, lastName } = useSelector((state) => state.auth);

  return (
    <div className="bg-white rounded-xl overflow-hidden">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-5">
        <h1 className="text-2xl font-bold text-white">
          Payment Terms Agreement
        </h1>
        <p className="text-blue-100 mt-1">Mentor Services</p>
      </div>

      <div className="p-6 md:p-8">
        {/* Introduction */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-8 rounded">
          <p className="text-gray-700">
            By clicking "<span className="font-semibold">Confirm & Pay</span>"
            at checkout, you (
            <span className="font-medium">{`${firstName} ${lastName}`}</span>)
            confirm that you understand and accept the following terms related
            only to payment for mentoring services delivered by{" "}
            <span className="font-medium">{mentorName}</span>:
          </p>
        </div>

        <div className="space-y-8">
          {/* Fees Section */}
          <section className="transition-all hover:bg-gray-50 rounded-lg p-4">
            <h2 className="flex items-center text-lg font-semibold text-gray-900 mb-3">
              <span className="flex items-center justify-center bg-blue-600 text-white rounded-full w-8 h-8 mr-3 flex-shrink-0">
                1
              </span>
              Fees
            </h2>
            <ul className="ml-11 text-gray-700 space-y-2">
              <li className="flex items-start">
                <svg
                  className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>
                  The total price displayed at checkout (
                  <span className="font-medium">${fee}</span>) is the full cost
                  for the selected mentoring service.
                </span>
              </li>
              <li className="flex items-start">
                <svg
                  className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>
                  All prices are listed in USD and include/exclude applicable
                  taxes as shown.
                </span>
              </li>
            </ul>
          </section>

          {/* Payment Due Section */}
          <section className="transition-all hover:bg-gray-50 rounded-lg p-4">
            <h2 className="flex items-center text-lg font-semibold text-gray-900 mb-3">
              <span className="flex items-center justify-center bg-blue-600 text-white rounded-full w-8 h-8 mr-3 flex-shrink-0">
                2
              </span>
              Payment Due
            </h2>
            <ul className="ml-11 text-gray-700 space-y-2">
              <li className="flex items-start">
                <svg
                  className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>
                  The Fee is payable in full, upfront at the time of booking.
                </span>
              </li>
              <li className="flex items-start">
                <svg
                  className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>
                  Your session(s) are not confirmed until payment is
                  successfully processed and you receive a confirmation email.
                </span>
              </li>
            </ul>
          </section>

          {/* Non-Refundable Section */}
          <section className="transition-all hover:bg-gray-50 rounded-lg p-4">
            <h2 className="flex items-center text-lg font-semibold text-gray-900 mb-3">
              <span className="flex items-center justify-center bg-blue-600 text-white rounded-full w-8 h-8 mr-3 flex-shrink-0">
                3
              </span>
              Non-Refundable
            </h2>
            <ul className="ml-11 text-gray-700 space-y-2">
              <li className="flex items-start">
                <svg
                  className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>
                  All payments are final and non-refundable. No credits,
                  transfers, or partial refunds will be issued for
                  cancellations, rescheduling, or missed sessions, except where
                  consumer-protection law requires otherwise.
                </span>
              </li>
            </ul>
          </section>

          {/* Rescheduling Section */}
          <section className="transition-all hover:bg-gray-50 rounded-lg p-4">
            <h2 className="flex items-center text-lg font-semibold text-gray-900 mb-3">
              <span className="flex items-center justify-center bg-blue-600 text-white rounded-full w-8 h-8 mr-3 flex-shrink-0">
                4
              </span>
              Rescheduling
            </h2>
            <ul className="ml-11 text-gray-700 space-y-2">
              <li className="flex items-start">
                <svg
                  className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>
                  If you need to change a confirmed appointment, you may request
                  a new time at least 24 hours in advance; approval is subject
                  to mentor availability.
                </span>
              </li>
              <li className="flex items-start">
                <svg
                  className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>
                  Reschedule requests made with less than 24 hours' notice, or
                  failure to attend, will result in forfeiture of that session
                  with no refund.
                </span>
              </li>
            </ul>
          </section>

          {/* Chargebacks & Disputes Section */}
          <section className="transition-all hover:bg-gray-50 rounded-lg p-4">
            <h2 className="flex items-center text-lg font-semibold text-gray-900 mb-3">
              <span className="flex items-center justify-center bg-blue-600 text-white rounded-full w-8 h-8 mr-3 flex-shrink-0">
                5
              </span>
              Chargebacks & Disputes
            </h2>
            <ul className="ml-11 text-gray-700 space-y-2">
              <li className="flex items-start">
                <svg
                  className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>
                  You agree not to initiate a chargeback or payment dispute
                  without first contacting us to resolve any billing concerns.
                  Unauthorized chargebacks may be referred to collections.
                </span>
              </li>
            </ul>
          </section>

          {/* Acknowledgment Section */}
          <section className="transition-all hover:bg-gray-50 rounded-lg p-4">
            <h2 className="flex items-center text-lg font-semibold text-gray-900 mb-3">
              <span className="flex items-center justify-center bg-blue-600 text-white rounded-full w-8 h-8 mr-3 flex-shrink-0">
                6
              </span>
              Acknowledgment
            </h2>
            <ul className="ml-11 text-gray-700 space-y-2">
              <li className="flex items-start">
                <svg
                  className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>
                  By proceeding with payment, you affirm that you have read,
                  understood, and agree to be bound solely by these Payment
                  Terms.
                </span>
              </li>
            </ul>
          </section>
        </div>

        {/* Agreement Section */}
        <div className="mt-10 pt-6 border-t border-gray-200 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-500">Last updated: April 9, 2025</p>
          {/* <button className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-sm transition duration-150 ease-in-out">
            I Accept These Terms
          </button> */}
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditionsComponent;
